(() => {
  const initAbout = (about) => {
    const root = about.closest('[data-rpg-product-lab]');
    const gallery = root?.querySelector('[data-rpg-igdb-gallery]');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const desktopViewport = matchMedia('(min-width: 900px)');

    if (root) {
      const upperLayout = root.querySelector('.rpg-product-lab__layout');
      const mediaStage = root.querySelector('.rpg-product-lab__media-stage');
      const reserveArtworkSpace = () => {
        if (!upperLayout || !mediaStage || !desktopViewport.matches) {
          upperLayout?.style.removeProperty('--rpg-upper-artwork-height');
          mediaStage?.style.removeProperty('--rpg-product-sticky-top');
          return;
        }
        const stageHeight = mediaStage.getBoundingClientRect().height;
        const layoutRect = upperLayout.getBoundingClientRect();
        const layoutDocumentTop = layoutRect.top + window.scrollY;
        const centeredOffset = Math.max(16, (layoutRect.height - stageHeight) / 2);
        upperLayout.style.setProperty('--rpg-upper-artwork-height', `${stageHeight}px`);
        mediaStage.style.setProperty('--rpg-product-sticky-top', `${layoutDocumentTop + centeredOffset}px`);
      };
      if ('ResizeObserver' in window && mediaStage) {
        const artworkSpaceObserver = new ResizeObserver(reserveArtworkSpace);
        artworkSpaceObserver.observe(mediaStage);
        artworkSpaceObserver.observe(upperLayout);
      }
      desktopViewport.addEventListener('change', reserveArtworkSpace);
      requestAnimationFrame(reserveArtworkSpace);
      window.addEventListener('load', reserveArtworkSpace, { once: true });
      document.fonts?.ready.then(reserveArtworkSpace);
    }

    if (gallery && root) {
      const artwork = root.querySelector('.rpg-game-card__artwork-shell');
      const syncDesktopGalleryWidth = () => {
        if (!desktopViewport.matches || !artwork) {
          gallery.style.removeProperty('width');
          return;
        }
        const enlargedWidth = Math.min(artwork.getBoundingClientRect().width * 1.6, root.clientWidth);
        gallery.style.width = `${enlargedWidth}px`;
      };
      if ('ResizeObserver' in window && artwork) new ResizeObserver(syncDesktopGalleryWidth).observe(artwork);
      desktopViewport.addEventListener('change', syncDesktopGalleryWidth);
      requestAnimationFrame(syncDesktopGalleryWidth);
    }

    if (gallery) {
      const slides = [...gallery.querySelectorAll('[data-rpg-igdb-slide]')];
      const controls = [...gallery.querySelectorAll('[data-rpg-igdb-control]')];
      const controlsRail = gallery.querySelector('.rpg-product-lab__igdb-controls');
      const previous = gallery.querySelector('[data-rpg-igdb-previous]');
      const next = gallery.querySelector('[data-rpg-igdb-next]');
      const openOverlay = gallery.querySelector('[data-rpg-igdb-open-overlay]');
      const overlay = root.querySelector('[data-rpg-igdb-overlay]');
      const overlayImage = overlay?.querySelector('[data-rpg-igdb-overlay-image]');
      const overlayClose = overlay?.querySelector('[data-rpg-igdb-overlay-close]');
      const overlayPrevious = overlay?.querySelector('[data-rpg-igdb-overlay-previous]');
      const overlayNext = overlay?.querySelector('[data-rpg-igdb-overlay-next]');
      let active = 0;
      let timer;
      let paused = false;
      let railSyncFrame = 0;
      const syncRailEdges = () => {
        if (!controlsRail) return;
        gallery.classList.toggle('has-left-overflow', controlsRail.scrollLeft > 2);
        gallery.classList.toggle('has-right-overflow', controlsRail.scrollLeft + controlsRail.clientWidth < controlsRail.scrollWidth - 2);
      };
      const queueRailSync = () => {
        if (railSyncFrame) return;
        railSyncFrame = requestAnimationFrame(() => {
          railSyncFrame = 0;
          syncRailEdges();
        });
      };
      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === active);
          slide.setAttribute('aria-hidden', i === active ? 'false' : 'true');
        });
        controls.forEach((control, i) => {
          control.classList.toggle('is-active', i === active);
          control.setAttribute('aria-current', i === active ? 'true' : 'false');
        });
        if (controlsRail && controls.length) {
          const orderedControls = controls.map((_, offset) => controls[(active - 1 + offset + controls.length) % controls.length]);
          orderedControls.forEach((control) => controlsRail.append(control));
          controlsRail.scrollLeft = 0;
        }
        if (overlayImage) {
          overlayImage.src = slides[active].currentSrc || slides[active].src;
          overlayImage.alt = slides[active].alt.replace('gameplay screenshot', 'enlarged gameplay screenshot');
        }
        queueRailSync();
      };
      const stop = () => clearInterval(timer);
      const start = () => {
        stop();
        if (slides.length < 2 || paused || reducedMotion.matches || document.hidden) return;
        timer = setInterval(() => show(active + 1), 11000);
      };
      controls.forEach((control, index) => control.addEventListener('click', () => { show(index); start(); }));
      previous?.addEventListener('click', () => { show(active - 1); start(); });
      next?.addEventListener('click', () => { show(active + 1); start(); });
      const launchOverlay = () => {
        if (!overlay || !desktopViewport.matches) return;
        paused = true;
        stop();
        overlay.showModal();
        document.documentElement.classList.add('rpg-dialog-open');
      };
      openOverlay?.addEventListener('click', launchOverlay);
      openOverlay?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        launchOverlay();
      });
      overlayPrevious?.addEventListener('click', () => show(active - 1));
      overlayNext?.addEventListener('click', () => show(active + 1));
      overlayClose?.addEventListener('click', () => overlay.close());
      overlay?.addEventListener('click', (event) => { if (event.target === overlay) overlay.close(); });
      overlay?.addEventListener('close', () => {
        document.documentElement.classList.remove('rpg-dialog-open');
        paused = false;
        start();
      });
      controlsRail?.addEventListener('scroll', queueRailSync, { passive: true });
      if ('ResizeObserver' in window && controlsRail) new ResizeObserver(queueRailSync).observe(controlsRail);
      gallery.addEventListener('focusin', () => { paused = true; stop(); });
      gallery.addEventListener('focusout', () => { paused = false; start(); });
      document.addEventListener('visibilitychange', start);
      show(0);
      queueRailSync();
      start();
    }

    const mobileSlides = [...(root?.querySelectorAll('[data-rpg-igdb-mobile-slide]') || [])];
    if (mobileSlides.length && root) {
      const mediaStage = root.closest('[data-rpg-product-lab]')?.querySelector('.rpg-product-lab__media-stage');
      const mobileGallery = root.querySelector('[data-rpg-igdb-mobile-gallery]');
      let mobileActive = 0;
      let mobileTimer;
      let mobileFadeTimer;
      const setMobilePanDirection = (slide) => {
        if (!slide.naturalWidth || !slide.naturalHeight) return;
        const frame = slide.parentElement;
        if (!frame?.clientWidth || !frame.clientHeight) return;
        const imageRatio = slide.naturalWidth / slide.naturalHeight;
        const panVertically = imageRatio < 1;
        const renderedLength = panVertically
          ? frame.clientWidth / imageRatio
          : frame.clientHeight * imageRatio;
        const frameLength = panVertically ? frame.clientHeight : frame.clientWidth;
        const panDistance = Math.max(0, renderedLength - frameLength);
        slide.classList.toggle('rpg-product-lab__mobile-screenshot--pan-y', panVertically);
        slide.style.setProperty('--rpg-mobile-pan-distance', `-${panDistance.toFixed(2)}px`);
      };
      mobileSlides.forEach((slide) => {
        const prepareSlide = async () => {
          setMobilePanDirection(slide);
          try {
            await slide.decode();
          } catch (error) {
            // A completed image can still be displayed when decode() is unsupported.
          }
          slide.classList.add('is-loaded');
          if (slide.classList.contains('is-active')) {
            mobileGallery?.classList.remove('rpg-product-lab__mobile-screenshots--loading');
          }
        };
        if (slide.complete && slide.naturalWidth) prepareSlide();
        else slide.addEventListener('load', prepareSlide, { once: true });
      });
      const showMobile = (index) => {
        const nextActive = (index + mobileSlides.length) % mobileSlides.length;
        const outgoing = mobileSlides[mobileActive];
        if (nextActive !== mobileActive) {
          clearTimeout(mobileFadeTimer);
          outgoing?.classList.add('is-leaving');
          outgoing?.classList.remove('is-active');
        }
        mobileActive = nextActive;
        const incoming = mobileSlides[mobileActive];
        incoming.classList.remove('is-active', 'is-leaving');
        void incoming.offsetWidth;
        incoming.classList.add('is-active');
        if (outgoing && outgoing !== mobileSlides[mobileActive]) {
          mobileFadeTimer = setTimeout(() => outgoing.classList.remove('is-leaving'), 1000);
        }
        const next = mobileSlides[(mobileActive + 1) % mobileSlides.length];
        if (next?.src) new Image().src = next.src;
      };
      const syncMobile = () => {
        clearInterval(mobileTimer);
        clearTimeout(mobileFadeTimer);
        mobileSlides.forEach(setMobilePanDirection);
        const screenshotsVisible = root.classList.contains('rpg-product-lab--about-expanded') || mediaStage?.classList.contains('rpg-product-lab__media-stage--screenshots-front');
        if (!screenshotsVisible) {
          showMobile(0);
          return;
        }
        mobileSlides.forEach((slide) => slide.classList.remove('is-active', 'is-leaving'));
        void root.offsetWidth;
        mobileSlides[mobileActive].classList.add('is-active');
        if (mobileSlides.length < 2 || reducedMotion.matches || document.hidden) return;
        mobileTimer = setInterval(() => showMobile(mobileActive + 1), 7500);
      };
      let mobileExpandedState = root.classList.contains('rpg-product-lab--about-expanded');
      new MutationObserver(() => {
        const nextExpandedState = root.classList.contains('rpg-product-lab--about-expanded');
        if (nextExpandedState === mobileExpandedState) return;
        mobileExpandedState = nextExpandedState;
        syncMobile();
      }).observe(root, { attributes: true, attributeFilter: ['class'] });
      if (mediaStage) {
        let screenshotsVisibleState = mediaStage.classList.contains('rpg-product-lab__media-stage--screenshots-front');
        new MutationObserver(() => {
          const nextScreenshotsVisibleState = mediaStage.classList.contains('rpg-product-lab__media-stage--screenshots-front');
          if (nextScreenshotsVisibleState === screenshotsVisibleState) return;
          screenshotsVisibleState = nextScreenshotsVisibleState;
          syncMobile();
        }).observe(mediaStage, { attributes: true, attributeFilter: ['class'] });
      }
      document.addEventListener('visibilitychange', syncMobile);
      root.addEventListener('rpg:mobile-about-change', syncMobile);
      showMobile(0);
    }
  };

  const init = (root = document) => root.querySelectorAll('[data-rpg-igdb-about]').forEach(initAbout);
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', () => init(), { once: true })
    : init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
